import get from "lodash.get";
import moment from "moment";
import AreaUtils from "services/areas/utils";
import MapApi from "services/map/api";
import FlowApi from "./api";
import FlowUtils from "./utils";
import FlowActionTypes from "./types";

class FlowActioner {
  static setGeneralStats(stats) {
    return {
      type: FlowActionTypes.SET_GENERAL_STATS,
      stats,
    };
  }

  static setAttendanceGraph(data) {
    return {
      type: FlowActionTypes.SET_ATTENDANCE_GRAPH,
      data,
    };
  }

  static setTravelTimeGraph(data) {
    return {
      type: FlowActionTypes.SET_TRAVEL_TIME_GRAPH,
      data,
    };
  }

  static setChronologyGraph(data) {
    return {
      type: FlowActionTypes.SET_CHRONOLOGY_GRAPH,
      data,
    };
  }

  static setDetailsStats(stats) {
    return {
      type: FlowActionTypes.SET_DETAILS_STATS,
      stats,
    };
  }

  static setDistributionStats(stats) {
    return {
      type: FlowActionTypes.SET_DISTRIBUTION_STATS,
      stats,
    };
  }

  static setMap(map) {
    return {
      type: FlowActionTypes.SET_MAP_INFOS,
      map,
    };
  }

  static setSelectedLayers(layers) {
    return {
      type: FlowActionTypes.SET_SELECTED_LAYERS,
      layers,
    };
  }

  static setLoading(statName) {
    return {
      type: FlowActionTypes.SET_LOADING,
      statName,
    };
  }

  static setAutoRefresh(autoRefresh) {
    return {
      type: FlowActionTypes.SET_AUTO_REFRESH,
      autoRefresh,
    };
  }

  static setInitialized(value) {
    return {
      type: FlowActionTypes.SET_INITIALIZED,
      value,
    };
  }

  static setFilter = (key, value) => {
    return {
      type: FlowActionTypes.SET_FILTER,
      key,
      value,
    };
  };

  static setAttendanceFilter = (key, value) => {
    return {
      type: FlowActionTypes.SET_ATTENDANCE_FILTER,
      key,
      value,
    };
  };

  static setTravelTimeFilter = (key, value) => {
    return {
      type: FlowActionTypes.SET_TRAVEL_TIME_FILTER,
      key,
      value,
    };
  };

  static setChronologyTimeFilter = (key, value) => {
    return {
      type: FlowActionTypes.SET_CHRONOLOGY_TIME_FILTER,
      key,
      value,
    };
  };

  static setAttendanceTimeLimit = (key, value) => {
    return {
      type: FlowActionTypes.SET_ATTENDANCE_TIME_LIMIT,
      key,
      value,
    };
  };

  static setChronologyTimeLimit = (key, value) => {
    return {
      type: FlowActionTypes.SET_CHRONOLOGY_TIME_LIMIT,
      key,
      value,
    };
  };

  static setTravelTimeLimit = (key, value) => {
    return {
      type: FlowActionTypes.SET_TRAVEL_TIME_LIMIT,
      key,
      value,
    };
  };

  static setFlowTrafficInfos = (data) => {
    return {
      type: FlowActionTypes.SET_TRAFFIC_INFOS,
      data,
    };
  };

  static setCategoryCountGraph = (data) => {
    return {
      type: FlowActionTypes.SET_CATEGORY_COUNT_GRAPH,
      data,
    };
  };

  static setCategoryCountTimeFilter = (key, value) => {
    return {
      type: FlowActionTypes.SET_CATEGORY_COUNT_TIME_FILTER,
      key,
      value,
    };
  };

  static setTrafficChart = (data) => {
    return {
      type: FlowActionTypes.SET_TRAFFIC_CHART,
      data,
    };
  };

  static setTrafficChartTimeFilter = (key, value) => {
    return {
      type: FlowActionTypes.SET_TRAFFIC_CHART_TIME_FILTER,
      key,
      value,
    };
  };

  static setTrafficChartInitialized = (value) => {
    return {
      type: FlowActionTypes.SET_TRAFFIC_CHART_INITIALIZED,
      value,
    };
  };

  static setDefaultFilters = (settings) => {
    return (dispatch) => {
      const from = Math.floor(
        moment()
          .subtract(
            Math.abs(get(settings, "flow.nb_days_from_date", -1)),
            "day"
          )
          .startOf("day")
          .unix()
      );

      const to = Math.floor(
        moment()
          .subtract(Math.abs(get(settings, "flow.nb_days_to_date", -1)), "day")
          .endOf("day")
          .unix()
      );

      dispatch(FlowActioner.setFilter("from", from));
      dispatch(FlowActioner.setFilter("to", to));
      dispatch(
        FlowActioner.setFilter(
          "availableFlowTypes",
          settings?.flow?.types_allowed || []
        )
      );
      dispatch(
        FlowActioner.setFilter("flowTypes", settings?.flow?.types_allowed || [])
      );
      dispatch(
        FlowActioner.setFilter(
          "distributions",
          settings?.flow?.paths?.distributions || []
        )
      );
      dispatch(
        FlowActioner.setFilter(
          "maximumDurationInMinutes",
          settings?.flow?.paths?.maximum_duration_in_minutes
        )
      );
      dispatch(
        FlowActioner.setFilter(
          "durationDistributionUnit",
          settings?.flow?.paths?.duration_distribution_unit
        )
      );
      dispatch(
        FlowActioner.setAttendanceTimeLimit(
          "timeLimit",
          settings?.flow?.attendance
        )
      );
      dispatch(
        FlowActioner.setChronologyTimeLimit(
          "timeLimit",
          settings?.flow?.chronology
        )
      );
      dispatch(
        FlowActioner.setTravelTimeLimit("timeLimit", {
          left_time_limit: settings?.flow?.paths?.left_time_limit ?? 0,
          right_time_limit: settings?.flow?.paths?.right_time_limit ?? 24,
        })
      );
      dispatch(FlowActioner.setFilter("initialized", true));
    };
  };

  static setTrafficInfos = (settings) => {
    return (dispatch) => {
      dispatch(FlowActioner.setFlowTrafficInfos(settings?.flow?.traffic));
    };
  };

  static retrieveAllStats = () => {
    return (dispatch, getState) => {
      dispatch(FlowActioner.setInitialized(false));
      const FlowState = getState().Flow;
      const selectedLayer = FlowUtils.getSelectedLayer(FlowState);
      const type = selectedLayer?.type;
      const apiType = selectedLayer?.apiType;
      const filters = FlowUtils.getFilters(type, getState());
      if (
        filters?.flow_types === undefined ||
        filters?.from === 0 ||
        filters?.to === 0
      ) {
        return Promise.resolve(null);
      }
      let promises = [];

      if (!selectedLayer) return Promise.resolve(null);

      if (
        FlowUtils.isRequestAllowed(
          "general",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        promises = [
          ...promises,
          dispatch(FlowActioner.retrieveGeneralStats(selectedLayer)),
        ];
      }

      if (
        FlowUtils.isRequestAllowed(
          "details",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        promises = [
          ...promises,
          dispatch(FlowActioner.retrieveDetailsStats(selectedLayer)),
        ];
      }

      if (
        FlowUtils.isRequestAllowed(
          "chronology",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        const xAxis = FlowState?.filters?.chronology?.x_axis || "24H";
        const options = {};
        if (xAxis === "24H") {
          options.x_axis = "HOUR";
          options.group_by_hours = true;
        } else {
          options.x_axis = xAxis;
          options.group_by_hours = false;
        }
        promises = [
          ...promises,
          dispatch(
            FlowActioner.retrieveChronologyGraph(selectedLayer, options)
          ),
        ];
      }

      if (
        FlowUtils.isRequestAllowed(
          "categoryCount",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        const xAxis = FlowState?.filters?.categoryCount?.x_axis || "HOUR";
        const options = { x_axis: xAxis, group_by_hours: false };
        promises = [
          ...promises,
          dispatch(
            FlowActioner.retrieveCategoryCountGraph(selectedLayer, options)
          ),
        ];
      }

      if (
        FlowUtils.isRequestAllowed(
          "attendance",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        promises = [
          ...promises,
          dispatch(
            FlowActioner.retrieveAttendanceGraph(selectedLayer, {
              group_by_hours: false,
            })
          ),
        ];
      }

      if (
        FlowUtils.isRequestAllowed(
          "distribution",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        promises = [
          ...promises,
          dispatch(FlowActioner.retrieveDistributionStats(selectedLayer)),
        ];
      }

      if (
        FlowUtils.isRequestAllowed(
          "travelTime",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        promises = [
          ...promises,
          dispatch(FlowActioner.retrieveTravelTimeGraph(selectedLayer)),
        ];
      }

      if (
        FlowUtils.isRequestAllowed(
          "trafficChart",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        promises = [
          ...promises,
          dispatch(FlowActioner.retrieveTrafficChart(selectedLayer)),
        ];
      }

      return Promise.all(promises).then(() => {
        dispatch(FlowActioner.setInitialized(true));
      });
    };
  };

  static retrieveGeneralStats(layer) {
    return (dispatch, getState) => {
      const FlowState = getState().Flow;
      const type = layer?.type;
      const apiType = layer?.apiType;
      const filters = FlowUtils.getFilters(type, getState());

      if (
        !FlowUtils.isRequestAllowed(
          "general",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        return null;
      }

      if (!get(FlowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("general"));
      }

      const promise =
        apiType === "TRAFFIC"
          ? FlowApi.getTrafficGeneralStats(layer?.id, filters)
          : FlowApi.getGeneralStats(layer?.id, type, filters);

      return promise
        .then((stats) => {
          dispatch(FlowActioner.setGeneralStats({ apiType, type, ...stats }));
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveDetailsStats(layer) {
    return (dispatch, getState) => {
      const FlowState = getState().Flow;
      const type = layer?.type;
      const apiType = layer?.apiType;
      const filters = FlowUtils.getFilters(type, getState());

      if (
        !FlowUtils.isRequestAllowed(
          "details",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        return null;
      }

      if (!get(FlowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("details"));
      }

      return FlowApi.getDetailsStats(layer?.id, type, filters)
        .then((stats) => {
          const formattedStats = FlowUtils.formatDetailsStatsFromApi(
            {
              type,
              apiType: layer?.apiType,
              [`${type}_id`]: layer?.id, // point_id ; path_id ; area_id
              reqFilters: filters,
              ...stats,
            },
            FlowState
          );
          dispatch(FlowActioner.setDetailsStats(formattedStats));
          return Promise.resolve(stats);
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveDistributionStats(layer) {
    return (dispatch, getState) => {
      const flowState = getState().Flow;
      const type = layer?.type;
      const apiType = layer?.apiType;
      const filters = FlowUtils.getFilters(type, getState());
      const data = FlowUtils.getDistributionBody(layer?.id, getState());

      if (
        !FlowUtils.isRequestAllowed(
          "distribution",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        return null;
      }

      if (!get(flowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("distribution"));
      }

      return FlowApi.getDistributionStats(layer?.id, type, filters, data)
        .then((stats) => {
          const distributionValuesTotal = stats?.distribution
            .map((d) => d.values.map((v) => v.value))
            .flat()
            .reduce((acc, value) => (acc += value), 0);

          const formattedStats = {
            type,
            ...(type === "path" ? { path_id: layer?.id } : {}),
            reqFilters: filters,
            ...stats,
            distribution: FlowUtils.sortDistributionData(
              stats.distribution.map((d) => {
                return {
                  ...d,
                  values: d.values.map((value) => ({
                    ...value,
                    percentage: value.value / distributionValuesTotal,
                  })),
                };
              }),
              data?.distribution?.map((item) => item?.name)
            ),
          };

          const formattedDistributionPercentages =
            FlowUtils.percentDistribution(
              formattedStats.distribution.map((s) => s.values).flat()
            );

          // Apply the new formated percentages to the existing distributions values
          let formattedDistributionPercentageIndex = 0;
          formattedStats.distribution.map((distribution) => {
            return distribution.values.forEach((value) => {
              value.percentage =
                formattedDistributionPercentages[
                  formattedDistributionPercentageIndex
                ].percentage;
              formattedDistributionPercentageIndex++;
            });
          });

          dispatch(FlowActioner.setDistributionStats(formattedStats));
          return Promise.resolve(stats);
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveLayersInfos() {
    return (dispatch, getState) => {
      const FlowState = getState().Flow;

      if (!get(FlowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("details"));
      }

      const contractId = getState().Contract.contract.id;
      const LayersInfosPromises = [
        MapApi.getAreasInfos({
          contract_id: contractId,
          types: ["FLOW", "PARKANDFLOW", "UNEQUIPPED", "PEDESTRIANANDFLOW"],
        }),
        MapApi.getPointsInfos({
          contract_id: contractId,
          types: ["FLOW", "UNEQUIPPED", "VIRTUAL"],
        }),
        MapApi.getPathsInfos({
          contract_id: contractId,
          types: ["FLOW", "UNEQUIPPED", "VIRTUAL", "TRAFFIC"],
        }),
      ];

      return Promise.all(LayersInfosPromises)
        .then(([areasInfos, pointsInfos, pathsInfos]) => {
          const mapFlowConfig = get(getState(), "Map.config.flow", {});

          AreaUtils.sortAreasByConfigMapAreasOrder(
            areasInfos.areas,
            mapFlowConfig.areas
          );

          // List of Points / Areas / Paths from API
          const formattedMapInfos = FlowUtils.formatLayersInfosFromApi(
            { ...areasInfos, ...pointsInfos, ...pathsInfos },
            mapFlowConfig
          );

          const currentFloor = getState()?.Map?.config?.currentFloor || 0;
          let currentSelectedLayer = FlowUtils.getSelectedLayer(
            getState()?.Flow
          );

          if (currentSelectedLayer?.floor_level !== currentFloor) {
            currentSelectedLayer = null;
          }

          dispatch(FlowActioner.setMap(formattedMapInfos));
          dispatch(
            FlowActioner.setSelectedLayers([
              currentSelectedLayer ||
                FlowUtils.getDefaultSelectedLayer(
                  formattedMapInfos,
                  currentFloor
                ),
            ])
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveAttendanceGraph(layer, options = {}) {
    return (dispatch, getState) => {
      const FlowState = getState().Flow;
      const type = layer?.type;
      const apiType = layer?.apiType;
      const filters = FlowUtils.getAttendanceFilters(type, getState());
      if (
        filters?.flow_types === undefined ||
        filters?.from === 0 ||
        filters?.to === 0
      ) {
        return Promise.resolve(null);
      }
      if (
        !FlowUtils.isRequestAllowed(
          "attendance",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        return null;
      }

      if (!get(FlowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("attendance"));
      }

      const updatedFilters = {
        ...filters,
        ...options,
      };

      return FlowApi.getAttendanceGraph(layer?.id, type, updatedFilters)
        .then((data) => {
          dispatch(FlowActioner.setAttendanceGraph({ type, ...data }));
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveCategoryCountGraph(layer, options = {}) {
    return (dispatch, getState) => {
      const FlowState = getState().Flow;
      const type = layer?.type;
      const apiType = layer?.apiType;
      const filters = FlowUtils.getFilters(type, getState());
      const map = get(FlowState, "map", {});
      if (
        filters?.flow_types === undefined ||
        filters?.from === 0 ||
        filters?.to === 0
      ) {
        return Promise.resolve(null);
      }
      if (
        !FlowUtils.isRequestAllowed(
          "categoryCount",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        return null;
      }

      if (!get(FlowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("categoryCount"));
      }

      const updatedFilters = {
        ...filters,
        ...options,
      };

      return FlowApi.getChronologyGraph(layer?.id, type, updatedFilters)
        .then((data) => {
          const formattedGraph = FlowUtils.formatCategoryCountGraphFromApi(
            { type, ...data },
            map
          );
          dispatch(FlowActioner.setCategoryCountGraph(formattedGraph));
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveTravelTimeGraph(layer) {
    return (dispatch, getState) => {
      const FlowState = getState().Flow;
      const type = layer?.type;
      const apiType = layer?.apiType;
      const filters = FlowUtils.getTravelTimeFilters(type, getState());
      if (
        filters?.flow_types === undefined ||
        filters?.from === 0 ||
        filters?.to === 0
      ) {
        return Promise.resolve(null);
      }
      if (
        !FlowUtils.isRequestAllowed(
          "travelTime",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        return null;
      }

      if (!get(FlowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("travelTime"));
      }

      return FlowApi.getTravelTimeGraph(layer?.id, type, filters)
        .then((data) => {
          dispatch(FlowActioner.setTravelTimeGraph({ type, ...data }));
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveTrafficChart = (layer, options = {}) => {
    return (dispatch, getState) => {
      dispatch(FlowActioner.setTrafficChartInitialized(false));
      const FlowState = getState().Flow;
      const type = layer?.type;
      const apiType = layer?.apiType;
      const filters = {
        ...FlowState.filters.trafficChart,
        ...FlowUtils.getFilters(type, getState()),
      };

      if (filters?.from === 0 || filters?.to === 0) {
        return Promise.resolve(null);
      }

      if (
        !FlowUtils.isRequestAllowed(
          "trafficChart",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        return null;
      }

      if (!get(FlowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("trafficChart"));
      }

      const updatedFilters = {
        from: filters.from,
        to: filters.to,
        x_axis: filters.x_axis,
        duration_unit: filters.duration_unit,
        ...options,
      };

      return FlowApi.getTrafficGraph(layer?.id, updatedFilters)
        .then((data) => {
          dispatch(FlowActioner.setTrafficChart(data));
          dispatch(FlowActioner.setTrafficChartInitialized(true));
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  };

  static retrieveChronologyGraph(layer, options = {}) {
    return (dispatch, getState) => {
      const FlowState = getState().Flow;
      const type = layer?.type;
      const apiType = layer?.apiType;
      const filters = FlowUtils.getFilters(type, getState());
      const map = get(FlowState, "map", {});
      if (
        filters?.flow_types === undefined ||
        filters?.from === 0 ||
        filters?.to === 0
      ) {
        return Promise.resolve(null);
      }
      if (
        !FlowUtils.isRequestAllowed(
          "chronology",
          type,
          type === "path" ? apiType.toLowerCase() : undefined
        )
      ) {
        return null;
      }

      if (!get(FlowState, "autoRefresh", false)) {
        dispatch(FlowActioner.setLoading("chronology"));
      }

      const updatedFilters = {
        ...filters,
        ...options,
      };

      return FlowApi.getChronologyGraph(layer?.id, type, updatedFilters)
        .then((data) => {
          const formattedGraph = FlowUtils.formatChronologyGraphFromApi(
            { type, ...data },
            map
          );
          dispatch(FlowActioner.setChronologyGraph(formattedGraph));
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }
}

export default FlowActioner;
